/**
 * Semantic model definitions. These are authored by engineers in /semantic,
 * reviewed in PRs, and are the ONLY way data becomes queryable. Users compose
 * metrics and dimensions; they never write SQL or joins.
 */

export type DialectName = "sqlite" | "postgres" | "clickhouse" | "snowflake";

/** Raw SQL fragment. `{{t}}` is replaced with the quoted alias of the owning model. */
export type SqlFragment = string | Partial<Record<DialectName, string>>;

export type Aggregation = "sum" | "count" | "count_distinct" | "avg" | "min" | "max";

/**
 * additive:      can be summed across any dimension (sales, item count)
 * semi_additive: can be summed across some dimensions but not time (open tabs, balances)
 * non_additive:  can never be summed (distinct counts, averages, ratios)
 */
export type Additivity = "additive" | "semi_additive" | "non_additive";

export type DimensionType = "string" | "number" | "boolean" | "time";
export const TIME_GRAINS = ["hour", "day", "week", "month", "quarter", "year", "day_of_week"] as const;
export type TimeGrain = (typeof TIME_GRAINS)[number];
/** `day_of_week` folds every Monday in the range into one bucket, and so on: ISO weekday numbers, 1 = Monday. */
export const GRAIN_LABELS: Record<TimeGrain, string> = {
  hour: "hour", day: "day", week: "week", month: "month", quarter: "quarter", year: "year", day_of_week: "day of week",
};
export type Format = "currency" | "number" | "integer" | "percent" | "duration";

export type Relationship = "many_to_one" | "one_to_one" | "one_to_many";

export interface DimensionDef {
  label: string;
  description?: string;
  type: DimensionType;
  /** Column on the model's table. Mutually exclusive with `sql`. */
  column?: string;
  sql?: SqlFragment;
  /** Allowed grains for time dimensions (defaults to all). */
  grains?: TimeGrain[];
  format?: Format;
  /** Hidden dimensions are joinable/filterable but not shown in the catalog. */
  hidden?: boolean;
  /**
   * Rows where this dimension is empty (NULL) don't belong to any of its values, so
   * they're left out whenever a query groups or filters by it (e.g. ledger entries
   * with no payment type when grouping by payment type).
   */
  excludeEmpty?: boolean;
  /**
   * Another model's dimension this one is the same as (`"days.fiscal_day"`). A query grouped or
   * filtered by either reads whichever of them each metric's model reaches most directly, its own
   * column first, so models with no join between them still line up on it (labor and sales by
   * business day). Left out of the catalog, which lists the one it's the same as.
   */
  sameAs?: string;
}

export interface MeasureDef {
  label: string;
  /** Plain-English, user-facing definition. Required: every number must explain itself. */
  description: string;
  agg: Aggregation;
  column?: string;
  sql?: SqlFragment;
  /** Row-level predicate applied inside the aggregate, e.g. `{{t}}.voided = 0`. */
  filter?: SqlFragment;
  /** Human-readable version of `filter`, shown in "show your work". */
  filterDescription?: string;
  additivity?: Additivity;
  format?: Format;
  certified: boolean;
  owner: string;
  /** Result cache TTL override. */
  freshnessTtlSeconds?: number;
  /**
   * The same metric at a finer grain, used automatically when a query needs
   * groupings this model can't reach (e.g. daily summary → individual ledger
   * lines to break sales down by product). The output keeps this measure's key.
   * Only link measures that are reconciled to be equal.
   */
  detail?: MeasureDetail;
}

export interface MeasureDetail {
  /** `model.measure` with the same aggregation and format. */
  measure: string;
  /** This model's dimensions → the detail model's equivalent (`sales.fiscal_day` → `sales_lines.fiscal_day`). */
  dimensions: Record<string, string>;
}

export interface JoinDef {
  model: string;
  relationship: Relationship;
  /** Column(s) on this model. Use an array for composite keys (e.g. `["tab_user_id", "location_id"]`). */
  from: string | string[];
  /** Column(s) on the target model, in the same order as `from`. */
  to: string | string[];
  /**
   * Rows with no join key (NULL `from`) aren't tied to any target record, so they're
   * left out whenever a query groups or filters by a field reached through this join,
   * instead of showing up as an empty group.
   */
  excludeUnmatched?: boolean;
  /**
   * Compare the keys as text. For keys stored as a number on one side and text on the other, where
   * the database would otherwise cast the text and fail on a stray non-numeric value (e.g. '').
   */
  textKeys?: boolean;
}

export const joinColumns = (c: string | string[]): string[] => (Array.isArray(c) ? c : [c]);

export type ScopeDef =
  /** This model's table has the location column directly. */
  | { column: string }
  /** Scope is inherited through a many_to_one join (by join name). */
  | { via: string }
  /**
   * Each row is a group of scopes (a brand and its stores), through a one_to_many join (by join name) to
   * the model whose table has the location column. For data that belongs to the group, not to any one
   * scope in it (a brand's email). A viewer sees a group, and every row scoped through it, only if they
   * can see every scope in the group; staff see every group. The scope picker doesn't narrow it: a group's
   * figures are always the whole group's.
   */
  | { groups: string };

export interface ChangelogEntry {
  version: string;
  date: string;
  note: string;
  /** Field names on this model affected by the change. */
  fields?: string[];
  breaking?: boolean;
}

/**
 * live:        rows appear as activity happens and are final.
 * day_close:   figures are only final after each location's fiscal day closes and
 *              the ledger is audited; the current (still open) day is provisional.
 * after_close: rows only appear once a location's fiscal day closes; the current
 *              day is missing entirely.
 */
export type Freshness = "live" | "day_close" | "after_close";

export interface ModelDef {
  name: string;
  label: string;
  description?: string;
  /** Table or view, optionally schema-qualified (`v2.sales_daily`). */
  table: string;
  primaryKey: string;
  scope: ScopeDef;
  /** Time dimension used to apply the query timeRange. May be `dim` or `join.dim`. */
  defaultTime?: string;
  /** Defaults to "live". */
  freshness?: Freshness;
  /** Large row-level tables: refuse queries without a date range. */
  requireTimeRange?: boolean;
  /** Internal models (e.g. line-level detail sources) don't appear in the catalog. */
  hidden?: boolean;
  /**
   * Access beyond seeing a location that this model needs (e.g. `"labor"` for wage data). A query
   * that touches it covers only the locations where the viewer has that access, as the workspace's
   * identity adapter says (`accessFor`). The name is the pack's; the identity adapter maps it to
   * the tenant's own permission.
   */
  access?: string;
  dimensions: Record<string, DimensionDef>;
  measures: Record<string, MeasureDef>;
  joins: Record<string, JoinDef>;
  version: string;
  changelog: ChangelogEntry[];
}

export function defaultAdditivity(agg: Aggregation): Additivity {
  return agg === "sum" || agg === "count" ? "additive" : "non_additive";
}
